import React from 'react';
import useCarousel from '../hooks/useCarousel';
import { stats, programComponents } from '../constants/programData';

import img1 from '../assets/images/volunteers/volunteers_1.jpg';
import img2 from '../assets/images/volunteers/volunteers_2.jpg';
import img3 from '../assets/images/volunteers/volunteers_3.jpg';
import img4 from '../assets/images/volunteers/volunteers_4.jpg';

const images = [img3, img4, img2, img1];

const ChevronLeft = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRight = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
  </svg>
);

const AboutSection = () => {
  const { currentIndex, next, prev, goTo, pause, resume } = useCarousel(images.length);

  return (
    <div id="about" className="container mx-auto px-4 sm:px-6 pt-6 pb-6 sm:py-16 relative z-10">
      <div className="glass-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">

          {/* Volunteer Carousel */}
          <div
            className="relative order-2 lg:order-1"
            onMouseEnter={pause}
            onMouseLeave={resume}
          >
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                {images.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`TRNF volunteers ${index + 1}`}
                    className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out ${
                      index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                  />
                ))}
              </div>

              <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between px-2 sm:px-3 z-20">
                <button
                  onClick={prev}
                  aria-label="Previous volunteer photo"
                  className="bg-black/50 text-white p-2 sm:p-3 rounded-full hover:bg-black/70 transition-all duration-300 backdrop-blur-sm min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <ChevronLeft />
                </button>
                <button
                  onClick={next}
                  aria-label="Next volunteer photo"
                  className="bg-black/50 text-white p-2 sm:p-3 rounded-full hover:bg-black/70 transition-all duration-300 backdrop-blur-sm min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <ChevronRight />
                </button>
              </div>

              <span className="sr-only" aria-live="polite">
                Volunteer photo {currentIndex + 1} of {images.length}
              </span>
            </div>

            <div className="flex justify-center gap-2 mt-3" role="tablist" aria-label="Volunteer photos">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goTo(index)}
                  role="tab"
                  aria-selected={index === currentIndex}
                  aria-label={`Go to volunteer photo ${index + 1}`}
                  className={`rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'w-4 h-2.5 bg-green-300'
                      : 'w-2.5 h-2.5 bg-white/40 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* About Content */}
          <div className="text-white order-1 lg:order-2">
            <h2 className="text-3xl lg:text-4xl font-bold mb-5 drop-shadow-xl text-center font-display">
              Our Community Impact
            </h2>

            <div className="space-y-4">
              <p className="text-base sm:text-lg lg:text-xl leading-relaxed drop-shadow-lg text-center lg:text-left">
                Therapeutic Riding at Normandy Farm (TRNF) guides riders through a comprehensive program of
                warm-ups, riding skills, horse care, and fun activities in a safe, indoor arena setting.
              </p>
              <p className="text-base sm:text-lg lg:text-xl leading-relaxed drop-shadow-lg text-center lg:text-left">
                Our team includes both seasoned individuals with years of experience and those with no prior
                experience, united by a willingness to contribute to their community.
              </p>

              {/* Impact Stats */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-6 mb-8">
                {stats.map(({ value, label }) => (
                  <div key={label} className="text-center bg-white/10 rounded-xl p-3 sm:p-5 backdrop-blur-sm border border-white/10">
                    <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-green-300 drop-shadow-lg font-display leading-none mb-1">
                      {value}
                    </div>
                    <div className="text-xs sm:text-sm text-white/80 drop-shadow-md mt-1">{label}</div>
                  </div>
                ))}
              </div>

              {/* Program Components */}
              <div id="services">
                <h2 className="text-3xl lg:text-4xl font-bold mb-5 drop-shadow-xl text-center font-display">
                  Our Program Components
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {programComponents.map(({ title, description, icon }) => (
                    <div
                      key={title}
                      className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-5 border border-white/20 transition-all duration-300 hover:bg-white/20 hover:border-white/30 hover:shadow-lg hover:-translate-y-0.5"
                    >
                      <div className="text-center">
                        <div className="w-11 h-11 mx-auto mb-3 bg-green-300/20 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {icon}
                          </svg>
                        </div>
                        <h4 className="text-base font-semibold mb-2 text-white">{title}</h4>
                        <p className="text-xs sm:text-sm text-white/80">{description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AboutSection;
